import React, {useEffect, useState} from 'react';
import {Box, Typography, Collapse, IconButton} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrTooltip from '../../common/arr-tooltip/ArrTooltip';
import {ReactComponent as AIIcon} from '../../../assets/images/ai-summary.svg';
import {ReactComponent as AIInactiveIcon} from '../../../assets/images/ai-summary-inactive.svg';
import './DocSummary.scss';
import {t} from 'i18next';

export interface DocSummaryProps {
  title: string | React.ReactElement;
  isExpanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  isDisabled?: boolean;
  tooltipText?: string;
  className?: string;
}

const DocSummary = ({
  title = '',
  isExpanded = false,
  onToggle,
  isDisabled = false,
  tooltipText = '',
  className = '',
  children,
}: React.PropsWithChildren<DocSummaryProps>) => {
  const [expanded, setExpanded] = useState(isExpanded);
  const summaryDisabled = isDisabled || !children;

  useEffect(() => {
    setExpanded(isExpanded);
  }, [isExpanded]);

  const handleToggle = () => {
    if (summaryDisabled) return;
    const newState = !expanded;
    setExpanded(newState);
    onToggle?.(newState);
  };

  const summaryTitle = !isDisabled
    ? title
    : `${title} - ${t('docView.aiGeneratedNoSummary')}`;

  return (
    <Box
      className={`doc-summary ${summaryDisabled ? 'disabled' : ''} ${expanded ? 'expanded' : 'collapsed'} ${className}`}
      data-testid="doc-summary-panel"
    >
      <Box
        className={`panel-header ${expanded ? 'expanded' : 'collapsed'}`}
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-disabled={summaryDisabled}
        data-testid="panel-header"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <Box className="panel-title" data-testid="panel-title">
          {isDisabled ? <AIInactiveIcon /> : <AIIcon />}

          <Typography
            variant="subtitle1"
            className={isDisabled ? 'title-label-inactive' : 'title-label'}
            data-testid="panel-title-label"
          >
            {summaryTitle}
          </Typography>

          {tooltipText && !summaryDisabled && (
            <ArrTooltip content={tooltipText}>
              <IconButton
                aria-label="Info"
                size="small"
                className="info-button"
                data-testid="info-button"
                onClick={(e) => e.stopPropagation()}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </ArrTooltip>
          )}
        </Box>
        {!isDisabled && (
          <Box className="toggle-indicator" data-testid="toggle-indicator">
            {expanded ? (
              <ExpandLessIcon data-testid="icon-less" />
            ) : (
              <ExpandMoreIcon data-testid="icon-more" />
            )}
          </Box>
        )}
      </Box>

      <Collapse in={expanded} timeout={300}>
        <Box className="panel-content" data-testid="panel-content">
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

export default React.memo(DocSummary);
